<div class="title">
  <p-heading [size]="'x-large'" [tag]="'h3'">Personal Travel Transactions</p-heading>

  <p-link
    [hideLabel]="(mediaMaxXs$ | async) ?? false"
    [iconSource]="'assets/icons/add.svg'"
    [variant]="'secondary'"
  >
    <a [routerLink]="'/personal-travel-transactions/create'">Create New Transaction</a>
  </p-link>
</div>

<p-divider aria-hidden="true" />

<section class="list">
  <div class="list-heading">
    <p-heading
      *ngrxLet="personalTravelTransactions$ | async as transactions"
      [size]="'small'"
      [tag]="'h4'"
    >
      {{ transactions?.totalElements }} Transactions
    </p-heading>

    <p-divider aria-hidden="true" [direction]="'vertical'" />

    <p-button-pure
      [iconSource]="'assets/icons/download.svg'"
      [loading]="(exporting$ | async) ?? false"
      [size]="'x-small'"
      [underline]="true"
      (click)="exportPersonalTravelTransactions()"
    >
      Export Personal Travel Transactions
    </p-button-pure>
  </div>

  <ng-container
    *ngrxLet="
      personalTravelTransactions$ as transactions;
      error as transactionsError;
      suspenseTpl: spinner
    "
  >
    <div *ngIf="transactionsError">
      <p-button-pure
        [hideLabel]="true"
        [iconSource]="'assets/icons/refresh.svg'"
        [size]="'large'"
        (click)="reloadPersonalTravelTransactions()"
      >
        Reload Transactions
      </p-button-pure>
    </div>

    <ng-container *ngIf="!transactionsError && transactions">
      <div class="overlay-spinner-host">
        <ng-container *ngIf="loading$ | async" [ngTemplateOutlet]="overlaySpinner" />

        <p-table caption="Personal Travel Transaction list table">
          <p-table-head *ngrxLet="sort$ as activeSort">
            <p-table-head-row>
              <p-table-head-cell
                *ngrxLet="{
                  label: 'Id',
                  sortId: 'id',
                } as cell"
                style="width: 1%"
              >
                <span>{{ cell.label }}</span>

                <opa-table-sort
                  [direction]="
                    !activeSort || activeSort.sortId !== cell.sortId
                      ? undefined
                      : activeSort.direction
                  "
                  (updateSort)="updateSort(cell.sortId, $event)"
                />
              </p-table-head-cell>

              <p-table-head-cell
                *ngrxLet="{
                  label: 'Person',
                  control: form.controls.name,
                  sortId: 'user.firstName',
                } as cell"
                style="width: 14%"
              >
                <span>{{ cell.label }}</span>

                <opa-table-filter [active]="!!cell.control.value">
                  <p-text-field-wrapper [label]="cell.label">
                    <input type="text" [formControl]="cell.control" />
                  </p-text-field-wrapper>
                </opa-table-filter>

                <opa-table-sort
                  [direction]="
                    !activeSort || activeSort.sortId !== cell.sortId
                      ? undefined
                      : activeSort.direction
                  "
                  (updateSort)="updateSort(cell.sortId, $event)"
                />
              </p-table-head-cell>

              <p-table-head-cell
                *ngrxLet="{
                  label: 'Purpose',
                  control: form.controls.tripPurposeId,
                  sortId: 'tripPurposeId',
                } as cell"
                style="width: 15%"
              >
                <span>{{ cell.label }}</span>

                <opa-table-filter
                  [active]="!!cell.control.value && cell.control.value !== allValue"
                >
                  <p-select-wrapper [native]="true" [label]="cell.label">
                    <select [formControl]="cell.control">
                      <option [value]="allValue">All</option>

                      <option
                        *ngFor="let tripPurpose of tripPurposes$ | async; trackBy: trackTripPurpose"
                        [value]="tripPurpose.id"
                      >
                        {{ tripPurpose.description }}
                      </option>
                    </select>
                  </p-select-wrapper>
                </opa-table-filter>

                <opa-table-sort
                  [direction]="
                    !activeSort || activeSort.sortId !== cell.sortId
                      ? undefined
                      : activeSort.direction
                  "
                  (updateSort)="updateSort(cell.sortId, $event)"
                />
              </p-table-head-cell>

              <p-table-head-cell
                *ngrxLet="{
                  label: 'Date',
                  controlFrom: form.controls.dateFrom,
                  controlTo: form.controls.dateTo,
                  sortId: 'dateUsed',
                } as cell"
                style="width: 15%"
              >
                <span>Date</span>

                <opa-table-filter [active]="!!cell.controlFrom.value || !!cell.controlTo.value">
                  <p-text-field-wrapper [label]="cell.label + ' From'">
                    <input type="date" [formControl]="cell.controlFrom" />
                  </p-text-field-wrapper>

                  <p-text-field-wrapper [label]="cell.label + ' To'">
                    <input type="date" [formControl]="cell.controlTo" />
                  </p-text-field-wrapper>
                </opa-table-filter>

                <opa-table-sort
                  [direction]="
                    !activeSort || activeSort.sortId !== cell.sortId
                      ? undefined
                      : activeSort.direction
                  "
                  (updateSort)="updateSort(cell.sortId, $event)"
                />
              </p-table-head-cell>

              <p-table-head-cell
                *ngrxLet="{
                  label: 'Amount',
                  sortId: 'amount',
                } as cell"
                style="width: 15%"
              >
                <span>{{ cell.label }}</span>

                <opa-table-sort
                  [direction]="
                    !activeSort || activeSort.sortId !== cell.sortId
                      ? undefined
                      : activeSort.direction
                  "
                  (updateSort)="updateSort(cell.sortId, $event)"
                />
              </p-table-head-cell>

              <p-table-head-cell
                *ngrxLet="{
                  label: 'Remaining',
                  sortId: 'amountAfter',
                } as cell"
                style="width: 15%"
              >
                <span>{{ cell.label }}</span>

                <opa-table-sort
                  [direction]="
                    !activeSort || activeSort.sortId !== cell.sortId
                      ? undefined
                      : activeSort.direction
                  "
                  (updateSort)="updateSort(cell.sortId, $event)"
                />
              </p-table-head-cell>

              <p-table-head-cell
                *ngrxLet="{
                  label: 'Description',
                  sortId: 'description',
                } as cell"
                style="width: 25%"
              >
                <span>{{ cell.label }}</span>

                <opa-table-sort
                  [direction]="
                    !activeSort || activeSort.sortId !== cell.sortId
                      ? undefined
                      : activeSort.direction
                  "
                  (updateSort)="updateSort(cell.sortId, $event)"
                />
              </p-table-head-cell>
            </p-table-head-row>
          </p-table-head>

          <p-table-body>
            <p-table-row
              *ngFor="
                let transaction of transactions.content | fillArray: transactions.pageable.pageSize;
                trackBy: trackRow
              "
            >
              <p-table-cell>
                <ng-container *ngIf="transaction; else emptyCell">
                  <a [routerLink]="['/personal-travel-transactions', transaction.id]">
                    {{ transaction.id }}
                  </a>
                </ng-container>
              </p-table-cell>
              <p-table-cell>
                <ng-container *ngIf="transaction; else emptyCell">
                  {{ transaction.user.firstName }} {{ transaction.user.lastName }}
                </ng-container>
              </p-table-cell>
              <p-table-cell>
                <ng-container *ngIf="transaction; else emptyCell">
                  {{ transaction.tripPurpose?.description || '-' }}
                </ng-container>
              </p-table-cell>
              <p-table-cell>
                <ng-container *ngIf="transaction; else emptyCell">
                  {{ transaction.dateUsed | date }}
                </ng-container>
              </p-table-cell>
              <p-table-cell>
                <ng-container *ngIf="transaction; else emptyCell">
                  {{
                    transaction.amountAfter - transaction.amountBefore | opaCurrency: null : true
                  }}
                </ng-container>
              </p-table-cell>
              <p-table-cell>
                <ng-container *ngIf="transaction; else emptyCell">
                  {{ transaction.amountAfter | opaCurrency }}
                </ng-container>
              </p-table-cell>
              <p-table-cell>
                <ng-container *ngIf="transaction; else emptyCell">
                  {{ transaction.description }}
                </ng-container>
              </p-table-cell>
            </p-table-row>
          </p-table-body>
        </p-table>
      </div>

      <p-pagination
        [totalItemsCount]="transactions.totalElements"
        [itemsPerPage]="transactions.size"
        [activePage]="transactions.pageable.pageNumber + 1"
        (update)="updatePageNo($event.detail.page)"
      />
    </ng-container>
  </ng-container>
</section>

<ng-template #spinner>
  <div class="loading-spinner"><p-spinner /></div>
</ng-template>

<ng-template #overlaySpinner>
  <div class="overlay-spinner"><p-spinner /></div>
</ng-template>

<ng-template #emptyCell>
  <span class="empty-cell"></span>
</ng-template>
